import { collectElementLabelIds } from "@/components/ui/tags/element-tags-chip-list";
import { isTagReadable } from "@/components/ui/tags/tag-accessibility";
import type { TreeData } from "@/utils/tree-generation/tree-data";
import { GUID } from "@faro-lotv/foundation";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { selectLightweightElementsSlice } from "../lightweight-elements-selectors";
import type { LightweightElementsState } from "../lightweight-elements-slice";
import { curryAppSelector } from "../reselect";
import type { RootState } from "../store";
import { selectTags } from "../tags/tags-selectors";
import { UNTAGGED, type Tag } from "../tags/tags-slice";
import { ProjectFilteringSlice } from "./project-filtering-slice";

type OnlyProjectFilteringStore = { projectFiltering: ProjectFilteringSlice };

/**
 * @returns The project filtering slice state
 */
export function selectProjectFilteringSlice({
  projectFiltering,
}: OnlyProjectFilteringStore): ProjectFilteringSlice {
  return projectFiltering;
}

/**
 * @returns The selected tags for project filtering
 */
export const selectSearchTags = createSelector(
  selectProjectFilteringSlice,
  (projectFiltering) => projectFiltering.tags,
);

/**
 * @returns The selected date range for project filtering
 */
export const selectSearchDateRange = createSelector(
  selectProjectFilteringSlice,
  (projectFiltering) => projectFiltering.dateRange,
);

/**
 * @returns The selected start date for project filtering
 */
export const selectSearchStartDate = createSelector(
  selectProjectFilteringSlice,
  (projectFiltering) => projectFiltering.dateRange.start,
);

/**
 * @returns The selected end date for project filtering
 */
export const selectSearchEndDate = createSelector(
  selectProjectFilteringSlice,
  (projectFiltering) => projectFiltering.dateRange.end,
);

/**
 * @returns The search text for project filtering
 */
export const selectSearchText = createSelector(
  selectProjectFilteringSlice,
  (projectFiltering) => projectFiltering.text,
);

/**
 * @returns The IDs of the project tags whose name exactly matches the current search text (case-insensitive)
 */
export const selectSearchTextMatchingTagIds = createSelector(
  selectSearchText,
  selectTags,
  (searchText, tags) => {
    const query = searchText.trim().toLocaleLowerCase();
    if (!query) return [];
    return tags
      .filter(
        (tag) =>
          tag.id !== UNTAGGED.id &&
          tag.name.trim().toLocaleLowerCase() === query,
      )
      .map((tag) => tag.id);
  },
);

export const selectSearchTagMatches = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      selectTags,
      selectSearchTextMatchingTagIds,
      (_state: RootState, element: TreeData["element"]) => element,
      (
        _state: RootState,
        _element: TreeData["element"],
        hasChildTreeNodes: boolean,
      ) => hasChildTreeNodes,
    ],
    (
      state: LightweightElementsState,
      tags: Tag[],
      matchingTagIds: GUID[],
      element: TreeData["element"],
      hasChildTreeNodes: boolean,
    ) => {
      if (matchingTagIds.length === 0 || !element) {
        return [];
      }

      // Collect the labels of this element and, when its children are not rendered
      // as their own tree nodes (e.g. the time points of a room), also of its descendants.
      const labelIds = collectElementLabelIds(
        element.id,
        !hasChildTreeNodes,
        state.lightweightElements,
        new Set<GUID>(),
      );

      // Tag needs to be the same as the searched text and also be a label of the element or one of its descendants.
      return tags.filter(
        (tag) =>
          matchingTagIds.includes(tag.id) &&
          labelIds.has(tag.id) &&
          isTagReadable(tag),
      );
    },
  ),
);

/**
 * @returns True if any tags are selected for filtering
 */
export const selectHasSearchTags = createSelector(
  selectSearchTags,
  (selectedTags) => selectedTags !== undefined && selectedTags.length > 0,
);

/**
 * @returns True if any date range is selected for filtering
 */
export const selectHasSearchDateRange = createSelector(
  selectSearchDateRange,
  (dateRange) => dateRange.start !== undefined || dateRange.end !== undefined,
);

/**
 * @returns True if search text is active for filtering
 */
export const selectHasSearchText = createSelector(
  selectSearchText,
  (searchText) => searchText.length > 0,
);

/**
 * @returns True if either tags or dates are selected (ignoring search text)
 */
export const selectHasSearchTagsOrDates = createSelector(
  selectHasSearchTags,
  selectHasSearchDateRange,
  (hasTags, hasDateRange) => hasTags || hasDateRange,
);

/**
 * @returns True if any filters are active (tags, date range or search text)
 */
export const selectHasActiveSearchFilters = createSelector(
  selectHasSearchTags,
  selectHasSearchDateRange,
  selectHasSearchText,
  (hasTags, hasDateRange, hasSearchText) =>
    hasTags || hasDateRange || hasSearchText,
);

/**
 * @returns True if a specific tag is selected for filtering
 * @param tagId The ID of the tag to check
 */
export const selectIsTagSelected = curryAppSelector(
  (projectFilteringSlice: OnlyProjectFilteringStore, tagId: GUID) => {
    const selectedTags = selectSearchTags(projectFilteringSlice);
    return selectedTags?.includes(tagId) ?? false;
  },
);

/**
 * @returns True if the date range includes the given date
 * @param date The date to check
 */
export const selectIsDateInRange = curryAppSelector(
  (projectFilteringSlice: OnlyProjectFilteringStore, date: ISOTimeString) => {
    const dateRange = selectSearchDateRange(projectFilteringSlice);
    const { start, end } = dateRange;

    if (!start && !end) {
      return true;
    }

    if (start && date < start) {
      return false;
    }

    if (end && date > end) {
      return false;
    }

    return true;
  },
);

/**
 * @returns The number of selected tags
 */
export const selectSearchTagsCount = createSelector(
  selectSearchTags,
  (selectedTags) => selectedTags?.length ?? 0,
);
